import { ref } from '@nuxtjs/composition-api';
import { AddItemParams, AddItemsParams } from '@shared/composables/useCartCookie/types';
import { getCookie, setCookie } from '@shared/helpers/cookies';
import { getCleanShopifyId } from '@shared/helpers/getCleanShopifyId';
import { Cart, LineItem, useCart } from '@unified-commerce/gpc-vue-storefront-shopify';

const SEARCH_CART = 'searchCart';

interface SearchCartItem {
  variantId: string;
  queryId?: string;
  compareToPrice?: number;
}
type SearchCart = SearchCartItem[];

const cookieCart = ref<SearchCart>([]);

const saveSearchCartToStorage = (cartValue: SearchCart, domain: string) => {
  setCookie(SEARCH_CART, JSON.stringify(cartValue), domain);
};

const initCart = (cart: Cart | null, domain: string) => {
  const cookieValue = getCookie(SEARCH_CART);
  let parsedCookieCart: SearchCart = [];

  try {
    parsedCookieCart = JSON.parse(cookieValue || '[]');
  } catch (error) {
    console.error(`${SEARCH_CART} cookie could not be parsed`);
  }

  if (Array.isArray(parsedCookieCart) && parsedCookieCart.length) {
    cookieCart.value = parsedCookieCart;
  } else {
    cookieCart.value = [];
  }

  const cookieCartMap = new Map<string, SearchCartItem>(
    cookieCart.value.map((item) => [item.variantId, item]),
  );

  // Rebuild cookieCart from current cart lineItems, preserving existing cookie info if available
  const syncedCartItems: SearchCartItem[] = (cart?.lineItems || [])
    .filter((lineItem) => !!getCleanShopifyId(lineItem.merchandiseId))
    .map((lineItem) => {
      const variantId = getCleanShopifyId(lineItem.merchandiseId);
      const existingItem = cookieCartMap.get(variantId);

      return existingItem
        ? existingItem // preserve existing item fully (including queryId or others)
        : {
            variantId,
            compareToPrice: lineItem.compareAtPriceV2 ?? undefined,
          };
    });

  // Only update if different to avoid unnecessary cookie writes
  const isDifferent =
    cookieCart.value.length !== syncedCartItems.length ||
    cookieCart.value.some((item, idx) => item.variantId !== syncedCartItems[idx].variantId);

  if (isDifferent) {
    cookieCart.value = syncedCartItems;
    saveSearchCartToStorage(cookieCart.value, domain);
  }
};

const useCartCookie = () => {
  const domain = window.location.hostname.replace('www', '');
  const {
    addItem: addItemToCart,
    addItems: addItemsToCart,
    removeItem: removeItemFromCart,
    updateItemQty,
    cart,
    loading,
    error,
    load,
    cartBuyerIdentityUpdate,
  } = useCart();

  initCart(cart.value, domain);

  const addItem = async ({ product, quantity, queryId, customQuery }: AddItemParams) => {
    await addItemToCart({ product, quantity, customQuery });

    const productVariantId = getCleanShopifyId(product.merchandiseId || product.variantId);

    const index = cookieCart.value.findIndex((i) => i.variantId === productVariantId);
    const item: SearchCartItem = {
      variantId: getCleanShopifyId(productVariantId),
      queryId,
    };
    if (index === -1) {
      cookieCart.value = [...cookieCart.value, item];
    } else {
      cookieCart.value[index] = item;
    }
    saveSearchCartToStorage(cookieCart.value, domain);
  };

  const addMultipleItems = async ({ products }: AddItemsParams) => {
    const mappedProducts = products.map((p) => {
      return {
        variantId: p.variantId,
        quantity: p.quantity,
      };
    });

    await addItemsToCart(mappedProducts);

    mappedProducts.forEach((p) => {
      const index = cookieCart.value.findIndex((i) => i.variantId === p.variantId);
      const item: SearchCartItem = {
        variantId: getCleanShopifyId(p.variantId),
      };
      if (index === -1) {
        cookieCart.value = [...cookieCart.value, item];
      } else {
        cookieCart.value[index] = item;
      }
      saveSearchCartToStorage(cookieCart.value, domain);
    });
  };

  const removeItem = async ({ product }: { product: LineItem }) => {
    await removeItemFromCart({ product });

    cookieCart.value = cookieCart.value.filter((item) => {
      return item.variantId !== getCleanShopifyId(product.merchandiseId);
    });

    saveSearchCartToStorage(cookieCart.value, domain);
  };

  return {
    cookieCart,
    addItem,
    addMultipleItems,
    removeItem,
    updateItemQty,
    load,
    cartBuyerIdentityUpdate,
    cart,
    loading,
    error,
  };
};

export default useCartCookie;
